import { useIntl } from 'react-intl';
import { Selector } from 'sonata-react-components';

import type { SelectorOptionPropsInterface } from 'sonata-react-components';

import './metricSortPanel.scss';

export type MetricSortField = 'baseline' | 'uplift' | 'cpsv';
export type MetricSortOrder = 'asc' | 'desc';
export type MetricSort = { field: MetricSortField; order: MetricSortOrder } | null;

export const DEFAULT_METRIC_SORT: { field: MetricSortField; order: MetricSortOrder } = {
  field: 'uplift',
  order: 'desc'
};

type Props = {
  metricSort: MetricSort;
  onChange: (metricSort: MetricSort) => void;
};

export function MetricSortPanel({ metricSort, onChange }: Readonly<Props>) {
  const { formatMessage } = useIntl();
  const displaySort = metricSort ?? DEFAULT_METRIC_SORT;

  const orderOptions: SelectorOptionPropsInterface[] = [
    {
      id: 'desc',
      label: formatMessage({ defaultMessage: 'High to low', id: 'driveToStore.poiSelectionModal.metricSort.highToLow' })
    },
    {
      id: 'asc',
      label: formatMessage({ defaultMessage: 'Low to high', id: 'driveToStore.poiSelectionModal.metricSort.lowToHigh' })
    }
  ];

  const metricOptions: SelectorOptionPropsInterface[] = [
    {
      id: 'baseline',
      label: formatMessage({ defaultMessage: 'Baseline', id: 'driveToStore.poiSelectionModal.metricSort.baseline' })
    },
    {
      id: 'uplift',
      label: formatMessage({ defaultMessage: 'Uplift', id: 'driveToStore.poiSelectionModal.metricSort.uplift' })
    },
    {
      id: 'cpsv',
      label: formatMessage({ defaultMessage: 'CPSV', id: 'driveToStore.poiSelectionModal.metricSort.cpsv' })
    }
  ];

  const selectedOrderOption = orderOptions.find(option => option.id === displaySort.order);
  const selectedMetricOption = metricOptions.find(option => option.id === displaySort.field);

  const handleOrderChange = (option: SelectorOptionPropsInterface) => {
    onChange({ ...displaySort, order: option.id as MetricSortOrder });
  };

  const handleMetricChange = (option: SelectorOptionPropsInterface) => {
    onChange({ ...displaySort, field: option.id as MetricSortField });
  };

  return (
    <div className="metricSortPanel">
      <p className="metricSortPanel__title">
        {formatMessage({ defaultMessage: 'Sort by:', id: 'driveToStore.poiSelectionModal.metricSort.title' })}
      </p>
      <hr className="metricSortPanel__divider" />
      <div className="metricSortPanel__field">
        <span className="metricSortPanel__label">
          {formatMessage({ defaultMessage: 'Order', id: 'driveToStore.poiSelectionModal.metricSort.order' })}
        </span>
        <Selector
          id="metricSortPanel-order"
          onChange={handleOrderChange}
          options={orderOptions}
          tiny
          type="simple"
          value={selectedOrderOption ? [selectedOrderOption] : undefined}
        />
      </div>
      <div className="metricSortPanel__field">
        <span className="metricSortPanel__label">
          {formatMessage({ defaultMessage: 'Metrics', id: 'driveToStore.poiSelectionModal.metricSort.metrics' })}
        </span>
        <Selector
          id="metricSortPanel-metric"
          onChange={handleMetricChange}
          options={metricOptions}
          tiny
          type="simple"
          value={selectedMetricOption ? [selectedMetricOption] : undefined}
        />
      </div>
    </div>
  );
}
